import React from "react";
import { Drawer, useMediaQuery, useTheme } from "@mui/material";
import styled from "styled-components";
import { iniciais } from "../../../../components/datagrid/iniciais";

type Status =
  | "success"
  | "warning"
  | "danger"
  | "disabled"
  | "ATENCAO"
  | "active"
  | "CRITICO"
  | "inactive"
  | "OK";

export type ProfileDetails = {
  id: string;
  name: string;
  location: string;
  email: string;
  totalKw: number;
  rankedValue: number; // pode ser negativo/positivo/zero
  status: Status;
  statusLabel: string;
  lastUpdate?: string;
  following?: boolean;
  /** Automação (prancheta DetalhePerfil): postagens e respostas de IA. */
  allowPosts?: boolean;
  allowAiCommentResponse?: boolean;
  /** Sincronização das palavras-chave. */
  pendingKeywords?: number;
  syncingKeywords?: number;
  noResultKeywords?: number;
  failedKeywords?: number;
  /** Ficha marcada como cópia no Google: ações de escrita ficam travadas. */
  copia?: boolean;
};

export type ReportType = "completo" | "winback";

type ProfileDetailsDialogProps = {
  open: boolean;
  profile: ProfileDetails | null;
  onClose: () => void;
  onEdit: (id: string | number) => void;
  onViewDetails: (id: string) => void;
  onDisableFollow?: (id: string | number) => void;
  showDisableFollow?: boolean;
  showEditAction?: boolean;
  onOpenReport?: (id: string, tipo: ReportType) => void;
  onCopyWinbackLink?: (id: string) => void;
  showReportActions?: boolean;
  /** Ranking e automação: só aparecem quando a página passa os handlers. */
  onSyncRadar?: (id: string) => void;
  onSyncSerp?: (id: string) => void;
  onTogglePosts?: (id: string, currentlyAllowed: boolean) => void;
  onToggleAi?: (id: string, currentlyAllowed: boolean) => void;
  /** Alguma ação deste perfil está em andamento (trava os botões). */
  busy?: boolean;
};

const TOM: Record<Status, { fundo: string; cor: string; ponto: string }> = {
  success: { fundo: "#EAF7EE", cor: "#137333", ponto: "#1E8E3E" },
  active: { fundo: "#EAF7EE", cor: "#137333", ponto: "#1E8E3E" },
  OK: { fundo: "#EAF7EE", cor: "#137333", ponto: "#1E8E3E" },
  warning: { fundo: "#FFF7E0", cor: "#B06000", ponto: "#F9AB00" },
  ATENCAO: { fundo: "#FFF7E0", cor: "#B06000", ponto: "#F9AB00" },
  danger: { fundo: "#FCE8E6", cor: "#B3261E", ponto: "#D93025" },
  CRITICO: { fundo: "#FCE8E6", cor: "#B3261E", ponto: "#D93025" },
  disabled: { fundo: "#F3F6FA", cor: "#5F6368", ponto: "#9AA0A6" },
  inactive: { fundo: "#F3F6FA", cor: "#5F6368", ponto: "#9AA0A6" },
};

const Painel = styled.aside`
  width: min(520px, 100vw);
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  font-family: var(--font-sans);
  color: #1f1f1f;
  .hint {
    font-size: 13px;
    line-height: 18px;
    color: #5f6368;
  }
  .lbl {
    font-family: var(--font-sans);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.6px;
    color: #80868b;
    text-transform: uppercase;
  }
  .material-icons {
    font-size: 20px;
  }
`;

const Cabecalho = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid #eef1f4;
`;

const Avatar = styled.span<{ $fundo: string; $cor: string }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${(p) => p.$fundo};
  color: ${(p) => p.$cor};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 500;
  flex-shrink: 0;
`;

const Badge = styled.span<{ $fundo?: string; $cor?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  background: ${(p) => p.$fundo ?? "#F3F6FA"};
  color: ${(p) => p.$cor ?? "#5F6368"};
  .material-icons {
    font-size: 14px;
  }
`;

const Corpo = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Kpis = styled.div`
  background: #fff;
  border: 1px solid #e2e6ea;
  border-radius: 16px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  overflow: hidden;
  .kpi {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 14px 16px;
    border-right: 1px solid #eef1f4;
    min-width: 0;
  }
  .kpi:last-child {
    border-right: 0;
  }
  .kpi b {
    font-family: var(--font-sans);
    font-size: 24px;
    font-weight: 500;
    letter-spacing: -0.5px;
  }
  @media (max-width: 400px) {
    .kpi {
      padding: 12px 10px;
    }
    .kpi .hint {
      overflow-wrap: anywhere;
    }
    .kpi b {
      font-size: 20px;
    }
  }
`;

const Botao = styled.button<{ $variante?: "filled" | "tonal" | "outlined" | "text" | "danger"; $pequeno?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: ${(p) => (p.$pequeno ? "36px" : "44px")};
  padding: ${(p) => (p.$pequeno ? "0 12px" : "0 18px")};
  border-radius: ${(p) => (p.$pequeno ? "10px" : "12px")};
  font-family: var(--font-sans);
  font-size: ${(p) => (p.$pequeno ? "13px" : "14px")};
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid
    ${(p) => (p.$variante === "outlined" ? "#E2E6EA" : p.$variante === "danger" ? "#F9DEDC" : "transparent")};
  background: ${(p) =>
    p.$variante === "filled" ? "#1264C7" : p.$variante === "tonal" ? "#EAF1FB" : p.$variante === "text" ? "transparent" : "#fff"};
  color: ${(p) =>
    p.$variante === "filled" ? "#fff" : p.$variante === "text" ? "#5F6368" : p.$variante === "danger" ? "#B3261E" : "#0B4FA8"};
  &:hover {
    filter: brightness(0.97);
  }
  &:disabled {
    opacity: 0.55;
    cursor: default;
    filter: none;
  }
  .material-icons {
    font-size: ${(p) => (p.$pequeno ? "18px" : "20px")};
  }
`;

const Linha = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #e2e6ea;
  border-radius: 12px;
  background: #fff;
  .ic {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: #eaf1fb;
    color: #0b4fa8;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .texto {
    display: flex;
    flex-direction: column;
    gap: 2px;
    /* Com dois botões ao lado, abaixo de ~180 px o texto empurra os botões para a linha de baixo. */
    flex: 1 1 180px;
    min-width: 0;
  }
  .titulo {
    font-size: 14px;
    font-weight: 500;
  }
`;

const Interruptor = styled.button<{ $on: boolean }>`
  width: 36px;
  height: 20px;
  border-radius: 999px;
  background: ${(p) => (p.$on ? "#1264C7" : "#BDC1C6")};
  position: relative;
  display: inline-block;
  border: 0;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: ${(p) => (p.$on ? "18px" : "2px")};
    width: 16px;
    height: 16px;
    border-radius: 999px;
    background: #fff;
    transition: left 0.15s;
  }
  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

const LinhaDeSync = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  padding: 10px 0;
  border-bottom: 1px solid #eef1f4;
  &:last-child {
    border-bottom: 0;
  }
`;

const Rodape = styled.div`
  padding: 14px 24px 20px;
  border-top: 1px solid #eef1f4;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #fff;
`;

const Fechar = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 0;
  background: transparent;
  color: #5f6368;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    background: #f1f3f4;
  }
`;

const Secao = ({ titulo, extra, children }: { titulo: string; extra?: React.ReactNode; children: React.ReactNode }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <span className="lbl">{titulo}</span>
      {extra}
    </div>
    {children}
  </div>
);

/** Situação das palavras-chave em um selo só (mesma leitura da coluna "Sincronização"). */
const seloDasPalavras = (p: ProfileDetails) => {
  if ((p.syncingKeywords ?? 0) > 0) return { texto: `Sincronizando ${p.syncingKeywords}`, fundo: "#EAF1FB", cor: "#0B4FA8" };
  if ((p.pendingKeywords ?? 0) > 0) return { texto: `${p.pendingKeywords} na fila`, fundo: "#FFF7E0", cor: "#B06000" };
  if ((p.failedKeywords ?? 0) > 0) return { texto: `${p.failedKeywords} com falha`, fundo: "#FCE8E6", cor: "#B3261E" };
  return { texto: p.lastUpdate ? `Concluída · ${p.lastUpdate}` : "Concluída", fundo: "#EAF7EE", cor: "#137333" };
};

export const ProfileDetailsDialog: React.FC<ProfileDetailsDialogProps> = ({
  open,
  profile,
  onClose,
  onEdit,
  onViewDetails,
  onDisableFollow,
  showDisableFollow,
  showEditAction = false,
  onOpenReport,
  onCopyWinbackLink,
  showReportActions = false,
  onSyncRadar,
  onSyncSerp,
  onTogglePosts,
  onToggleAi,
  busy = false,
}) => {
  const theme = useTheme();
  const telaCheia = useMediaQuery(theme.breakpoints.down("md"));

  if (!profile) return null;

  const tom = TOM[profile.status] ?? TOM.disabled;
  const ranqueadas = Math.abs(profile.rankedValue);
  const taxa = profile.totalKw > 0 ? Math.round((ranqueadas / profile.totalKw) * 100) : 0;
  const travado = busy || profile.copia === true;
  const temRanking = Boolean(onSyncRadar || onSyncSerp || onTogglePosts || onToggleAi);
  const palavras = seloDasPalavras(profile);
  const semResultado = profile.noResultKeywords ?? 0;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: telaCheia ? "100vw" : 520,
          maxWidth: "100vw",
          borderLeft: "1px solid #E2E6EA",
          boxShadow: "-12px 0 36px rgba(31,35,41,.12)",
        },
      }}
      slotProps={{ backdrop: { sx: { background: "rgba(31,35,41,.28)" } } }}
    >
      <Painel role="dialog" aria-label={`Detalhe do perfil ${profile.name}`}>
        <Cabecalho>
          <Avatar $fundo={tom.fundo} $cor={tom.cor}>{iniciais(profile.name)}</Avatar>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 18, fontWeight: 600, overflowWrap: "anywhere" }}>{profile.name}</span>
            <span className="hint" style={{ overflowWrap: "anywhere" }}>
              {[profile.location, profile.email].filter(Boolean).join(" · ")}
            </span>
            <div style={{ display: "flex", gap: 6, paddingTop: 4, flexWrap: "wrap" }}>
              <Badge $fundo={tom.fundo} $cor={tom.cor}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: tom.ponto }} />
                {profile.statusLabel}
              </Badge>
              {profile.lastUpdate && (
                <Badge>
                  <span className="material-icons">sync</span>
                  {`Atualizado ${profile.lastUpdate}`}
                </Badge>
              )}
            </div>
          </div>
          <Fechar type="button" aria-label="Fechar painel" onClick={onClose}>
            <span className="material-icons">close</span>
          </Fechar>
        </Cabecalho>

        <Corpo>
          <Kpis>
            <div className="kpi">
              <span className="hint">Total de palavras</span>
              <b>{profile.totalKw.toLocaleString("pt-BR")}</b>
            </div>
            <div className="kpi">
              <span className="hint">Ranqueadas</span>
              <b style={{ color: "#137333" }}>{ranqueadas.toLocaleString("pt-BR")}</b>
              <span className="hint">{`${taxa}%`}</span>
            </div>
            <div className="kpi">
              <span className="hint">Última leitura</span>
              <b style={{ fontSize: 16 }}>{profile.lastUpdate ?? "—"}</b>
              <span className="hint">Radar GeoRanking</span>
            </div>
          </Kpis>

          <div style={{ display: "flex", gap: 8 }}>
            {showEditAction && (
              <Botao
                type="button"
                $variante="filled"
                style={{ flex: 1 }}
                onClick={() => {
                  onClose();
                  onEdit(profile.id);
                }}
              >
                <span className="material-icons">edit_square</span>
                Editar perfil
              </Botao>
            )}
            <Botao type="button" $variante="outlined" style={{ flex: 1 }} onClick={() => onViewDetails(profile.id)}>
              <span className="material-icons">open_in_new</span>
              Abrir relatório
            </Botao>
          </div>
          <span className="hint" style={{ marginTop: -10 }}>“Abrir relatório” leva ao relatório atual do vendedor, sem alterações.</span>

          {showReportActions && onOpenReport && (
            <Secao titulo="Relatórios do cliente" extra={<Badge $fundo="#FFF7E0" $cor="#B06000">Só admin</Badge>}>
              <Linha>
                <span className="ic"><span className="material-icons">summarize</span></span>
                <div className="texto">
                  <span className="titulo">Relatório completo</span>
                  <span className="hint">Palavras, posições e evolução</span>
                </div>
                <Botao type="button" $variante="tonal" $pequeno aria-label="Abrir relatório completo" onClick={() => onOpenReport(profile.id, "completo")}>
                  Abrir
                </Botao>
              </Linha>
              <Linha>
                <span className="ic"><span className="material-icons">history</span></span>
                <div className="texto">
                  <span className="titulo">Relatório de retorno (winback)</span>
                  <span className="hint">Versão para reconquista do cliente</span>
                </div>
                <Botao type="button" $variante="tonal" $pequeno aria-label="Abrir relatório winback" onClick={() => onOpenReport(profile.id, "winback")}>
                  Abrir
                </Botao>
              </Linha>
              {onCopyWinbackLink && (
                <Linha>
                  <span className="ic"><span className="material-icons">link</span></span>
                  <div className="texto">
                    <span className="titulo">Link de retorno (24 h)</span>
                    <span className="hint">Abre o relatório de retorno sem login por 24 horas</span>
                  </div>
                  <Botao type="button" $variante="outlined" $pequeno aria-label="Copiar link de winback (24h)" onClick={() => onCopyWinbackLink(profile.id)}>
                    <span className="material-icons">content_copy</span>
                    Copiar
                  </Botao>
                </Linha>
              )}
            </Secao>
          )}

          {temRanking && (
            <Secao titulo="Ranking e automação">
              {(onSyncRadar || onSyncSerp) && (
                <Linha>
                  <span className="ic"><span className="material-icons">radar</span></span>
                  <div className="texto">
                    <span className="titulo">Atualizar ranking</span>
                    <span className="hint">{profile.copia ? "Ficha marcada como cópia: sincronização travada" : "Sincroniza as palavras-chave deste perfil"}</span>
                  </div>
                  {onSyncRadar && (
                    <Botao type="button" $variante="tonal" $pequeno disabled={travado} onClick={() => onSyncRadar(profile.id)}>
                      Via Radar
                    </Botao>
                  )}
                  {onSyncSerp && (
                    <Botao type="button" $variante="outlined" $pequeno disabled={travado} onClick={() => onSyncSerp(profile.id)}>
                      Via SerpAPI
                    </Botao>
                  )}
                </Linha>
              )}
              {onTogglePosts && (
                <Linha>
                  <span className="ic"><span className="material-icons">campaign</span></span>
                  <div className="texto">
                    <span className="titulo">Postagens automáticas</span>
                    <span className="hint">{profile.allowPosts ? "Ativas. Desligue para pausar as postagens deste perfil" : "Pausadas. Ligue para voltar a publicar neste perfil"}</span>
                  </div>
                  <Interruptor
                    type="button"
                    role="switch"
                    $on={!!profile.allowPosts}
                    aria-checked={!!profile.allowPosts}
                    aria-label={profile.allowPosts ? "Postagens automáticas ativas: clique para pausar" : "Postagens automáticas pausadas: clique para reativar"}
                    disabled={travado}
                    onClick={() => onTogglePosts(profile.id, !!profile.allowPosts)}
                  />
                </Linha>
              )}
              {onToggleAi && (
                <Linha>
                  <span className="ic"><span className="material-icons">forum</span></span>
                  <div className="texto">
                    <span className="titulo">Respostas de IA nas avaliações</span>
                    <span className="hint">{profile.allowAiCommentResponse ? "Ativas. Desligue para pausar as respostas automáticas" : "Pausadas. Ligue para a IA voltar a responder"}</span>
                  </div>
                  <Interruptor
                    type="button"
                    role="switch"
                    $on={!!profile.allowAiCommentResponse}
                    aria-checked={!!profile.allowAiCommentResponse}
                    aria-label={profile.allowAiCommentResponse ? "Respostas de IA nas avaliações ativas: clique para pausar" : "Respostas de IA nas avaliações pausadas: clique para reativar"}
                    disabled={travado}
                    onClick={() => onToggleAi(profile.id, !!profile.allowAiCommentResponse)}
                  />
                </Linha>
              )}
            </Secao>
          )}

          <Secao titulo="Sincronização">
            <div>
              <LinhaDeSync>
                <span>Palavras-chave</span>
                <Badge $fundo={palavras.fundo} $cor={palavras.cor}>{palavras.texto}</Badge>
              </LinhaDeSync>
              <LinhaDeSync>
                <span>Monitoramento</span>
                <Badge $fundo={profile.following === false ? "#F3F6FA" : "#EAF7EE"} $cor={profile.following === false ? "#5F6368" : "#137333"}>
                  {profile.following === false ? "Desativado" : "Ativo"}
                </Badge>
              </LinhaDeSync>
              <LinhaDeSync>
                <span>Radar</span>
                {semResultado > 0 ? (
                  <Badge $fundo="#FFF7E0" $cor="#B06000">{`${semResultado} ${semResultado === 1 ? "busca sem resultado" : "buscas sem resultado"}`}</Badge>
                ) : (
                  <Badge $fundo="#EAF7EE" $cor="#137333">Sem pendências</Badge>
                )}
              </LinhaDeSync>
            </div>
          </Secao>
        </Corpo>

        <Rodape>
          {showDisableFollow && onDisableFollow ? (
            <Botao type="button" $variante="danger" $pequeno onClick={() => onDisableFollow(profile.id)}>
              <span className="material-icons">visibility_off</span>
              Desativar monitoramento
            </Botao>
          ) : (
            <span />
          )}
          <Botao type="button" $variante="text" $pequeno onClick={onClose}>
            Fechar
          </Botao>
        </Rodape>
      </Painel>
    </Drawer>
  );
};
